/*
  Copyright (C) 2024 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { computed } from "vue";
import { marked } from "marked";
import { markedHighlight } from "marked-highlight";
import DOMPurify from "dompurify";
import hljs from "highlight.js";

// Configure marked to use highlight.js for code blocks via marked-highlight extension
marked.use(
  markedHighlight({
    langPrefix: "hljs language-",
    highlight(code, lang) {
      if (lang && hljs.getLanguage(lang)) {
        try {
          return hljs.highlight(code, { language: lang }).value;
        } catch (err) {
          console.warn("Highlight.js error:", err);
        }
      }
      // Use auto-detection if no language specified or language not found
      try {
        return hljs.highlightAuto(code).value;
        // eslint-disable-next-line no-unused-vars
      } catch (err) {
        return code;
      }
    },
  }),
);

/**
 * Highlight.js GitHub-like theme styles for code blocks.
 */
const HIGHLIGHT_STYLES = `
  /* Highlight.js GitHub-like theme */
  .hljs {
    color: #24292e;
    background: #f6f8fa;
  }
  .hljs-comment,
  .hljs-quote {
    color: #6a737d;
    font-style: italic;
  }
  .hljs-keyword,
  .hljs-selector-tag,
  .hljs-addition {
    color: #d73a49;
  }
  .hljs-number,
  .hljs-string,
  .hljs-meta .hljs-meta-string,
  .hljs-literal,
  .hljs-doctag,
  .hljs-regexp {
    color: #032f62;
  }
  .hljs-title,
  .hljs-section,
  .hljs-name,
  .hljs-selector-id,
  .hljs-selector-class {
    color: #6f42c1;
    font-weight: bold;
  }
  .hljs-attribute,
  .hljs-attr,
  .hljs-variable,
  .hljs-template-variable,
  .hljs-class .hljs-title,
  .hljs-type {
    color: #005cc5;
  }
  .hljs-symbol,
  .hljs-bullet,
  .hljs-subst,
  .hljs-meta,
  .hljs-meta .hljs-keyword,
  .hljs-selector-attr,
  .hljs-selector-pseudo,
  .hljs-link {
    color: #e36209;
  }
  .hljs-built_in,
  .hljs-deletion {
    color: #22863a;
  }
  .hljs-formula {
    background: #f6f8fa;
  }
  .hljs-emphasis {
    font-style: italic;
  }
  .hljs-strong {
    font-weight: bold;
  }
`;

/**
 * Default CSS styles for the iframe preview.
 * These styles provide a clean, readable presentation for Markdown/HTML content.
 */
const DEFAULT_PREVIEW_STYLES = `
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
    color: #333;
    margin: 1rem;
    line-height: 1.6;
  }
  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    margin-top: 1.5em;
    margin-bottom: 0.5em;
  }
  h1 { font-size: 2em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.25em; }
  p { margin-bottom: 1em; }
  ul { list-style-type: disc; padding-left: 2em; }
  ol { list-style-type: decimal; padding-left: 2em; }
  li { margin-bottom: 0.25em; }
  blockquote {
    margin-left: 0;
    padding-left: 1em;
    border-left: 3px solid #ccc;
    color: #666;
  }
  code {
    background-color: #f4f4f4;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.9em;
  }
  pre {
    background-color: #f4f4f4;
    padding: 1em;
    border-radius: 6px;
    overflow-x: auto;
  }
  pre code {
    background-color: transparent;
    padding: 0;
  }
  a { color: #0066cc; }
  a:hover { text-decoration: underline; }
  img { max-width: 100%; height: auto; }
  table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 1em;
  }
  th, td {
    border: 1px solid #ddd;
    padding: 0.5em;
    text-align: left;
  }
  th { background-color: #f4f4f4; }
  hr {
    border: none;
    border-top: 1px solid #ddd;
    margin: 2em 0;
  }
`;

// Flag to track if DOMPurify has been configured
let isDOMPurifyConfigured = false;

/**
 * Configures DOMPurify with security hooks.
 * This function is idempotent - it only configures DOMPurify once.
 */
function configureDOMPurify() {
  if (isDOMPurifyConfigured) {
    return;
  }

  // Force all links to open in a new tab with security attributes
  DOMPurify.addHook("afterSanitizeAttributes", function (node) {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  isDOMPurifyConfigured = true;
}

// Configure DOMPurify on module load
configureDOMPurify();

/**
 * Renders Markdown content to sanitized HTML.
 *
 * @param {string} content - The Markdown content to render
 * @returns {string} Sanitized HTML string
 */
export function renderMarkdown(content) {
  if (!content) {
    return "";
  }
  const rawHtml = marked.parse(content);
  return DOMPurify.sanitize(rawHtml);
}

/**
 * Sanitizes HTML content.
 *
 * @param {string} content - The HTML content to sanitize
 * @returns {string} Sanitized HTML string
 */
export function sanitizeHtml(content) {
  if (!content) {
    return "";
  }
  return DOMPurify.sanitize(content);
}

/**
 * Generates a complete HTML document for iframe preview.
 *
 * @param {string} bodyContent - The HTML content for the body
 * @param {string} customStyles - Optional custom CSS to add/override
 * @returns {string} Complete HTML document string
 */
export function generatePreviewDocument(bodyContent, customStyles = "") {
  const styles = customStyles
    ? `${DEFAULT_PREVIEW_STYLES}\n${HIGHLIGHT_STYLES}\n${customStyles}`
    : `${DEFAULT_PREVIEW_STYLES}\n${HIGHLIGHT_STYLES}`;

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <style>${styles}</style>
  </head>
  <body>
    ${bodyContent}
  </body>
  <script>
    window.addEventListener("message", function(e) {
      if (e.data && e.data.type === "preview-update") {
        document.body.innerHTML = e.data.html;
      }
    });
  ${"<"}/script>
</html>`;
}

/**
 * Vue composable for Markdown/HTML preview functionality.
 * Provides reactive preview content generation with proper sanitization.
 *
 * @param {Object} options - Configuration options
 * @param {import('vue').Ref<string>} options.content - Reactive reference to the content
 * @param {import('vue').Ref<string>} options.mimeType - Reactive reference to the MIME type
 * @param {string} options.customStyles - Optional custom CSS styles
 * @returns {Object} Composable API
 */
export function useMarkdownPreview(options = {}) {
  const { content, mimeType, customStyles = "" } = options;

  /**
   * Computed property that checks if the content is Markdown.
   */
  const isMarkdown = computed(() => {
    const type = mimeType?.value;
    return (
      type === "text/markdown" ||
      type === "text/x-markdown" ||
      type === "text/x-web-markdown"
    );
  });

  /**
   * Computed property that checks if the content is HTML.
   */
  const isHtml = computed(() => {
    return mimeType?.value === "text/html";
  });

  /**
   * Computed property that checks if content should be rendered as preview.
   */
  const shouldRenderPreview = computed(() => {
    return isMarkdown.value || isHtml.value;
  });

  /**
   * Computed property that generates the rendered/sanitized body HTML only.
   */
  const previewBodyHtml = computed(() => {
    if (!content?.value) {
      return "";
    }
    if (isMarkdown.value) {
      return renderMarkdown(content.value);
    } else if (isHtml.value) {
      return sanitizeHtml(content.value);
    }
    return "";
  });

  /**
   * Computed property that generates the complete iframe srcdoc content.
   */
  const iframeContent = computed(() => {
    return generatePreviewDocument(previewBodyHtml.value, customStyles);
  });

  return {
    isMarkdown,
    isHtml,
    shouldRenderPreview,
    previewBodyHtml,
    iframeContent,
    renderMarkdown,
    sanitizeHtml,
    generatePreviewDocument,
  };
}

// Export the default styles for customization
export { DEFAULT_PREVIEW_STYLES };
